import React from 'react';
import ReactDOM from 'react-dom';
import type { Root } from 'react-dom/client';

export const elementReady = ({ targetWindow = window, selector }) => {
  return new Promise(resolve => {
    const elm = targetWindow.document.querySelector(selector);

    if (elm) resolve(elm);

    new MutationObserver((non, observer) => {
      const elm = targetWindow.document.querySelector(selector);

      if (elm) {
        observer.disconnect();
        resolve(elm);
      }
    }).observe(targetWindow.document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
};

type AppendReactComponentToElementProps = {
  component: React.ComponentType<any>;
  domElement: Element;
  place?: 'first' | 'last' | null;
  props?: { scope?: { $on: (event: string, callback: () => void) => void } | null } & Record<string, any>;
  logzId?: string | null;
  wrapper?: Element | null;
  // The apps give createRoot from react-dom/client. OSD bundles this service with React 16, which has no
  // react-dom/client, so OSD gives none and the service uses the legacy render API.
  createRoot?: (container: Element) => Root;
};

// The legacy render API of React 16. React 19 removed it, so its types do not declare it.
const legacyReactDom = ReactDOM as unknown as {
  render: (element: React.ReactElement, container: Element) => void;
  unmountComponentAtNode: (container: Element) => boolean;
};

// One root for each container element. React warns when createRoot runs two times on the same element.
const roots = new WeakMap<Element, Root>();

export const appendReactComponentToElement = ({
  component,
  domElement,
  place = null,
  props = { scope: null },
  logzId = null,
  wrapper = null,
  createRoot,
}: AppendReactComponentToElementProps) => {
  if (logzId && document.getElementById(logzId)) return;

  let renderTo = domElement;

  const newDiv = wrapper || document.createElement('div');

  if (logzId) newDiv.id = logzId;

  if (place === 'first') {
    renderTo.prepend(newDiv);
    renderTo = newDiv;
  } else if (place === 'last') {
    renderTo.append(newDiv);
    renderTo = newDiv;
  } else if (logzId) renderTo.id = logzId;

  if (props.scope) {
    props.scope.$on('$destroy', () => {
      if (!createRoot) {
        legacyReactDom.unmountComponentAtNode(renderTo);

        return;
      }

      roots.get(renderTo)?.unmount();
      roots.delete(renderTo);
    });
  }

  const element = React.createElement(component, props);

  if (!createRoot) {
    legacyReactDom.render(element, renderTo);

    return;
  }

  const root = roots.get(renderTo) ?? createRoot(renderTo);

  roots.set(renderTo, root);
  root.render(element);
};

export const reactInjectorService = {
  elementReady,
  appendReactComponentToElement,
};
